import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

import { HubWorkbenchTable, hubWorkbenchTextCellClassName, hubWorkbenchTheadClassName } from "@/components/hub/HubWorkbenchTable";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { useHubToast } from "@/components/ui/HubToast";
import { Input } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import {
  fetchCartonQuantityTargets,
  saveCartonQuantityTargets,
  type CartonQuantityTargetRow,
} from "./prepackApi";

interface PrePackCartonTargetModalProps {
  open: boolean;
  shipmentId: number;
  onClose: () => void;
  onSaved: () => void;
}

const TARGET_HELP =
  "Each row is a carton. Move planned quantities across SKU columns. Packed amounts stay in the carton even when the target is lower. A target below what is packed blocks sealing until the extra quantity is removed with the normal pack controls. Sealed cartons cannot be edited. Quantity still on the batch is advisory only and is not part of this plan.";

function draftKey(cartons: readonly number[], rows: readonly CartonQuantityTargetRow[]): string {
  return JSON.stringify({
    cartons,
    rows: rows.map((row) => ({ key: row.key, quantities: row.quantities })),
  });
}

export function PrePackCartonTargetModal({
  open,
  shipmentId,
  onClose,
  onSaved,
}: PrePackCartonTargetModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const targetsQuery = useQuery({
    queryKey: ["prepack", "carton-quantity-targets", shipmentId],
    queryFn: () => fetchCartonQuantityTargets(shipmentId),
    enabled: open,
  });
  const [cartons, setCartons] = useState<number[]>([]);
  const [sealedCartons, setSealedCartons] = useState<number[]>([]);
  const [rows, setRows] = useState<CartonQuantityTargetRow[]>([]);
  const [advisories, setAdvisories] = useState<Array<{ sku: string; source: string; quantity: number }>>([]);
  const [savedKey, setSavedKey] = useState("");

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!targetsQuery.data) return;
    const nextCartons = targetsQuery.data.cartonNumbers;
    const nextRows = targetsQuery.data.rows.map((row) => ({ ...row, quantities: { ...row.quantities } }));
    setCartons(nextCartons);
    setSealedCartons(targetsQuery.data.sealedCartonNumbers ?? []);
    setRows(nextRows);
    setAdvisories(targetsQuery.data.advisories ?? []);
    setSavedKey(draftKey(nextCartons, nextRows));
  }, [targetsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => saveCartonQuantityTargets(shipmentId, rows),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["prepack"] });
      showToast({ message: "Carton quantity targets saved.", tone: "success" });
      onSaved();
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      showToast({ message, tone: "error" });
    },
  });

  const dirty = useMemo(() => draftKey(cartons, rows) !== savedKey, [cartons, rows, savedKey]);
  const balanceError = useMemo(() => {
    for (const row of rows) {
      const assigned = cartons.reduce((sum, carton) => sum + (row.quantities[String(carton)] ?? 0), 0);
      if (assigned > row.total) {
        return `${row.sku} targets ${String(assigned)}, which is more than ${String(row.total)} on the shipment.`;
      }
      const notTargeted = row.total - assigned;
      const overTarget = cartons.reduce((sum, carton) => {
        const packed = row.packedByCarton[String(carton)] ?? 0;
        const target = row.quantities[String(carton)] ?? 0;
        return sum + Math.max(0, packed - target);
      }, 0);
      if (notTargeted > overTarget) {
        return `${row.sku} still has ${String(notTargeted - overTarget)} not targeted. Put that quantity on a carton.`;
      }
    }
    return null;
  }, [cartons, rows]);

  if (!open) return null;

  function unassigned(row: CartonQuantityTargetRow): number {
    const assigned = cartons.reduce((sum, carton) => sum + (row.quantities[String(carton)] ?? 0), 0);
    return row.total - assigned;
  }

  function setQuantity(rowKey: string, carton: number, raw: string): void {
    if (sealedCartons.includes(carton)) return;
    const quantity = raw.trim() === "" ? 0 : Number(raw);
    if (!Number.isFinite(quantity) || quantity < 0) return;
    setRows((current) =>
      current.map((row) => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          quantities: { ...row.quantities, [String(carton)]: quantity },
        };
      }),
    );
  }

  function addCarton(): void {
    const next = cartons.length === 0 ? 1 : Math.max(...cartons) + 1;
    setCartons((current) => [...current, next]);
  }

  function cartonHasPacked(carton: number): boolean {
    const key = String(carton);
    return rows.some((row) => (row.packedByCarton[key] ?? 0) > 0);
  }

  function removeCarton(carton: number): void {
    if (cartonHasPacked(carton)) return;
    const key = String(carton);
    setCartons((current) => current.filter((value) => value !== carton));
    setRows((current) =>
      current.map((row) => {
        const quantities = { ...row.quantities };
        delete quantities[key];
        return { ...row, quantities };
      }),
    );
  }

  return createPortal(
    <div {...hubModalOverlayProps()} role="presentation">
      <div className="flex max-h-[85vh] w-[min(1100px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-2 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Carton quantity targets</h2>
          <HoverHintWithGlyph hoverHint={TARGET_HELP} />
        </div>
        {advisories.length > 0 ? (
          <p className="px-4 pb-2 text-sm text-amber-900">
            Still on the batch and not on a shipment yet:{" "}
            {advisories.map((advisory) => `${advisory.sku} ${String(advisory.quantity)}`).join(", ")}. Not in this plan.
          </p>
        ) : null}
        <div className="min-h-0 flex-1 overflow-hidden px-4">
          <HubWorkbenchTable
            className="max-h-[60vh]"
            loading={targetsQuery.isLoading}
            loadingMessage="Loading shipment quantities…"
            error={targetsQuery.isError ? "Could not load shipment quantities." : null}
          >
            <thead className={hubWorkbenchTheadClassName()}>
              <tr>
                <th className={hubWorkbenchTextCellClassName()}>Carton</th>
                {rows.map((row) => (
                  <th key={row.key} className={hubWorkbenchTextCellClassName({ align: "right" })}>
                    <span className="font-mono text-slate-800">{row.sku}</span>
                    <span className="mt-0.5 block font-sans text-[10px] font-normal text-slate-500">
                      {row.source === "serialized" ? "Serialized" : "Unserialized"} · {String(row.total)} on shipment
                    </span>
                    {row.mayStillJoin > 0 ? (
                      <span className="mt-0.5 block font-sans text-[10px] font-normal text-amber-800">
                        {String(row.mayStillJoin)} more may still join. Not in this plan.
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cartons.map((carton) => {
                const packedOnCarton = cartonHasPacked(carton);
                const sealed = sealedCartons.includes(carton);
                const removeBlocked = packedOnCarton || sealed;
                return (
                  <tr
                    key={carton}
                    className={
                      sealed
                        ? "border-t border-slate-200 bg-slate-100 text-slate-500"
                        : "border-t border-slate-200 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80"
                    }
                  >
                    <td className={hubWorkbenchTextCellClassName({ nowrap: true })}>
                      <span className="inline-flex items-center gap-1">
                        <ReadOnlyHoverLayer
                          active={removeBlocked}
                          hoverHint={
                            sealed
                              ? "This carton is sealed. Unseal it before changing the plan."
                              : "This carton already has packed or boxed quantity, so it cannot be removed."
                          }
                        >
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            aria-label={`Remove carton ${String(carton)}`}
                            disabled={removeBlocked}
                            onClick={() => removeCarton(carton)}
                          >
                            <X className="h-4 w-4" aria-hidden />
                          </Button>
                        </ReadOnlyHoverLayer>
                        Carton {String(carton)}
                        {sealed ? <span className="text-[10px] font-normal text-slate-500">Sealed</span> : null}
                      </span>
                    </td>
                    {rows.map((row) => {
                      const packed = row.packedByCarton[String(carton)] ?? 0;
                      const target = row.quantities[String(carton)] ?? 0;
                      return (
                        <td key={row.key} className={hubWorkbenchTextCellClassName({ align: "right" })}>
                          <Input
                            aria-label={`${row.sku} carton ${String(carton)} target`}
                            value={String(target)}
                            onChange={(event) => setQuantity(row.key, carton, event.target.value)}
                            inputMode="numeric"
                            disabled={sealed}
                            className="ml-auto h-8 w-20 text-right tabular-nums"
                          />
                          {packed > target ? (
                            <span className="mt-0.5 block text-[10px] text-rose-700">
                              {String(packed)} packed, {String(packed - target)} over target
                            </span>
                          ) : packed > 0 ? (
                            <span className="mt-0.5 block text-[10px] text-slate-500">{String(packed)} packed</span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-t border-slate-200">
                <td className={hubWorkbenchTextCellClassName()}>Not targeted</td>
                {rows.map((row) => (
                  <td key={row.key} className={hubWorkbenchTextCellClassName({ align: "right" })}>
                    {String(unassigned(row))}
                  </td>
                ))}
              </tr>
            </tbody>
          </HubWorkbenchTable>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
          <Button size="sm" variant="secondary" onClick={addCarton}>
            Add carton
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <HubSaveButton
              size="sm"
              dirty={dirty}
              disabled={balanceError != null}
              blockedHint={balanceError}
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save targets
            </HubSaveButton>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
